"use client"

import React, { useState, useEffect } from "react"
import {
  HiOutlineArrowRight,
  HiOutlineArrowUp,
  HiOutlineCalendar,
  HiOutlineDownload,
  HiOutlineDocumentText,
  HiOutlineFilter,
  HiOutlineViewGridAdd,
  HiOutlinePlus,
  HiOutlinePrinter,
  HiOutlineRefresh,
  HiOutlineSave,
  HiOutlineSearch,
  HiOutlineShare,
  HiOutlineTrendingUp,
  HiOutlineX,
  HiOutlineXCircle,
  HiOutlineDotsHorizontal,
  HiOutlineUserGroup,
  HiOutlineViewGrid,
} from "react-icons/hi"

import { Avatar, Badge, Button, Card, Progress, Spinner, Tooltip } from "flowbite-react"
import { TextInput as Input } from "flowbite-react"
import { Select } from "flowbite-react"

// Project interfaces
interface Project {
  id: string
  name: string
  status: string
  startDate: string
  endDate: string
  peopleAllocated: number
  epics: any[]
  progress: number
  onTimePercentage: number
  delayedCount: number
  resourceUtilization: number
  budgetPerformance: number
  avgCompletionTime: number
  teamMembers: TeamMember[]
  recentActivities: Activity[]
  riskScore: number
  upcomingMilestones: Milestone[]
  statusBreakdown: StatusCount[]
}

interface TeamMember {
  id: string
  name: string
  role: string
  avatar: string
  tasksAssigned: number
  tasksCompleted: number
}

interface Activity {
  id: string
  type: string
  description: string
  user: string
  timestamp: string
  entityType: string
  entityName: string
}

interface Milestone {
  id: string
  name: string
  dueDate: string
  status: string
  owner: string
  epicName?: string
}

interface StatusCount {
  status: string
  count: number
  percentage: number
}

interface ProjectDashboardPageProps {
  projectId: string
}

const ProjectDashboardPage: React.FC<ProjectDashboardPageProps> = ({ projectId }) => {
  const [project, setProject] = useState<Project | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLive, setIsLive] = useState(true)
  const [dateRange, setDateRange] = useState("Jan 26, 2025 - Apr 26, 2025")
  const [searchQuery, setSearchQuery] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [statusFilter, setStatusFilter] = useState("all")
  const [teamFilter, setTeamFilter] = useState("all")
  const [refreshing, setRefreshing] = useState(false)

  // Simulate fetching project data
  useEffect(() => {
    if (projectId) {
      setIsLoading(true)

      // Simulate API call delay
      setTimeout(() => {
        // Mock data based on the project ID
        const mockProject: Project = {
          id: projectId,
          name: "Cloud Migration",
          status: "On Track",
          startDate: "Jan 1, 2023",
          endDate: "Dec 31, 2023",
          peopleAllocated: 20,
          epics: [],
          progress: 67,
          onTimePercentage: 78,
          delayedCount: 5,
          resourceUtilization: 83,
          budgetPerformance: 92,
          avgCompletionTime: 18.5,
          teamMembers: [
            {
              id: "tm1",
              name: "Alex Johnson",
              role: "Project Manager",
              avatar: "",
              tasksAssigned: 12,
              tasksCompleted: 8,
            },
            {
              id: "tm2",
              name: "Sarah Chen",
              role: "Lead Developer",
              avatar: "",
              tasksAssigned: 24,
              tasksCompleted: 19,
            },
            {
              id: "tm3",
              name: "Michael Brown",
              role: "DevOps Engineer",
              avatar: "",
              tasksAssigned: 18,
              tasksCompleted: 15,
            },
            {
              id: "tm4",
              name: "Emily Davis",
              role: "UX Designer",
              avatar: "",
              tasksAssigned: 15,
              tasksCompleted: 12,
            },
          ],
          recentActivities: [
            {
              id: "act1",
              type: "status_change",
              description: "Changed status from 'Delayed' to 'On Track'",
              user: "Alex Johnson",
              timestamp: "2 hours ago",
              entityType: "Epic",
              entityName: "Database Migration",
            },
            {
              id: "act2",
              type: "comment",
              description: "Added comment: 'Security testing completed successfully'",
              user: "Sarah Chen",
              timestamp: "4 hours ago",
              entityType: "Feature",
              entityName: "Authentication System",
            }
          ],
          riskScore: 24,
          upcomingMilestones: [
            {
              id: "ms1",
              name: "Phase 1 Completion",
              dueDate: "May 15, 2025",
              status: "On Track",
              owner: "Alex Johnson",
              epicName: "Infrastructure Setup"
            },
            {
              id: "ms2",
              name: "Security Audit",
              dueDate: "May 30, 2025",
              status: "At Risk",
              owner: "Sarah Chen",
              epicName: "Security Framework"
            },
            {
              id: "ms3",
              name: "User Testing",
              dueDate: "June 10, 2025",
              status: "On Track",
              owner: "Emily Davis",
              epicName: "User Experience"
            },
          ],
          statusBreakdown: [
            { status: "On Track", count: 12, percentage: 48 },
            { status: "Delayed", count: 5, percentage: 20 },
            { status: "Completed", count: 6, percentage: 24 },
            { status: "Blocked", count: 2, percentage: 8 },
          ],
        }

        setProject(mockProject)
        setIsLoading(false)
      }, 800)
    }
  }, [projectId])

  const handleRefresh = () => {
    setRefreshing(true)
    setTimeout(() => {
      setRefreshing(false)
    }, 1500)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "On Track":
        return "success"
      case "Delayed":
      case "At Risk":
        return "warning"
      case "Blocked":
        return "failure"
      case "Completed":
        return "info"
      default:
        return "gray"
    }
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "status_change":
        return <HiOutlineRefresh className="h-4 w-4 text-blue-500" />
      case "comment":
        return <HiOutlineDocumentText className="h-4 w-4 text-emerald-500" />
      case "task_completed":
        return <HiOutlineDocumentText className="h-4 w-4 text-green-500" />
      case "document_upload":
        return <HiOutlineDocumentText className="h-4 w-4 text-purple-500" />
      default:
        return <HiOutlineDocumentText className="h-4 w-4 text-slate-500" />
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Spinner size="lg" />
          <p className="text-sm text-slate-600 dark:text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {project && (
        <div className="container mx-auto py-6 px-4">
          {/* Header */}
          <header className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <HiOutlineViewGridAdd className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{project.name} Dashboard</h1>
            </div>

            <div className="flex items-center gap-2">
              <Button
                color="light"
                size="sm"
                onClick={handleRefresh}
                className={refreshing ? "animate-spin" : ""}
              >
                <HiOutlineRefresh className="h-4 w-4" />
                <span className="ml-2">Refresh</span>
              </Button>
              
              
            </div>
          </header>

          {/* Project info bar */}
          <div className="bg-slate-50 dark:bg-slate-800 py-3 px-6 rounded-lg mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">Timeline</div>
                  <div className="text-sm font-medium text-slate-800 dark:text-white flex items-center gap-2">
                    <HiOutlineCalendar className="h-4 w-4 text-blue-500" />
                    {project.startDate} - {project.endDate}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">Team Size</div>
                  <div className="text-sm font-medium text-slate-800 dark:text-white flex items-center gap-2">
                    <HiOutlineUserGroup className="h-4 w-4 text-blue-500" />
                    {project.peopleAllocated} members
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge color={getStatusColor(project.status)}>
                  {project.status}
                </Badge>
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  Updated <span className="font-medium">today</span>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          <div className="space-y-6">
            {/* Top Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Strategic Map Progress */}
              <Card>
                <div className="p-4">
                  <h5 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                    Project Overall Progress
                  </h5>
                  <div className="flex flex-col items-center justify-center py-4">
                    <div className="relative h-36 w-36">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl font-bold text-slate-900 dark:text-white">
                            {project.progress}%
                          </div>
                          
                        </div>
                      </div>
                      <svg className="h-full w-full" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#e2e8f0"
                          strokeWidth="10"
                          className="dark:stroke-slate-700"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#fbbf24"
                          strokeWidth="10"
                          strokeDasharray={`${(project.progress || 0) * 2.51} 251`}
                          strokeDashoffset="0"
                          strokeLinecap="round"
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </Card>

              {/* On-time % */}
              <Card>
                <div className="p-4">
                  <h5 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                    On-time %
                  </h5>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-slate-900 dark:text-white">
                        {project.onTimePercentage}%
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Projects on schedule</div>
                    </div>
                    <div className="flex items-center gap-1 text-emerald-500">
                      <HiOutlineTrendingUp className="h-4 w-4" />
                      <span className="text-sm font-medium">+3%</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="h-[60px] w-full">
                      {/* Simple chart visualization */}
                      <div className="relative h-full">
                        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-slate-200 dark:bg-slate-700"></div>
                        <div className="absolute bottom-0 left-0 h-[40px] w-full">
                          <svg viewBox="0 0 100 20" className="h-full w-full">
                            <path
                              d="M0,10 Q10,5 20,8 T40,6 T60,10 T80,12 T100,8"
                              fill="none"
                              stroke="#10b981"
                              strokeWidth="1"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Delayed Count */}
              <Card>
                <div className="p-4">
                  <h5 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                    Delayed Count
                  </h5>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-slate-900 dark:text-white">
                        {project.delayedCount}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Avg. 12 days</div>
                    </div>
                    <div className="flex items-center gap-1 text-rose-500">
                      <HiOutlineArrowUp className="h-4 w-4" />
                      <span className="text-sm font-medium">+2</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="h-[60px] w-full">
                      {/* Placeholder for a small chart */}
                      <div className="flex items-end justify-between h-full">
                        <div className="w-[5%] bg-slate-200 dark:bg-slate-700 rounded-t h-[30%]"></div>
                        <div className="w-[5%] bg-slate-200 dark:bg-slate-700 rounded-t h-[40%]"></div>
                        <div className="w-[5%] bg-slate-200 dark:bg-slate-700 rounded-t h-[20%]"></div>
                        <div className="w-[5%] bg-slate-200 dark:bg-slate-700 rounded-t h-[60%]"></div>
                        <div className="w-[5%] bg-slate-200 dark:bg-slate-700 rounded-t h-[50%]"></div>
                        <div className="w-[5%] bg-slate-200 dark:bg-slate-700 rounded-t h-[70%]"></div>
                        <div className="w-[5%] bg-slate-200 dark:bg-slate-700 rounded-t h-[80%]"></div>
                        <div className="w-[5%] bg-rose-500 rounded-t h-[90%]"></div>
                        <div className="w-[5%] bg-rose-500 rounded-t h-[100%]"></div>
                        <div className="w-[5%] bg-rose-500 rounded-t h-[85%]"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Resource Utilization */}
              <Card>
                <div className="p-4">
                  <h5 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                    Resource Utilization
                  </h5>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-slate-900 dark:text-white">
                        {project.resourceUtilization}%
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Team capacity</div>
                    </div>
                    <div className="flex items-center gap-1 text-emerald-500">
                      <HiOutlineArrowUp className="h-4 w-4" />
                      <span className="text-sm font-medium">+7%</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="h-[60px] w-full">
                      {/* Placeholder for a small chart */}
                      <div className="relative h-full">
                        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-slate-200 dark:bg-slate-700"></div>
                        <div className="absolute bottom-0 left-[10%] right-0 h-[1px] bg-slate-200 dark:bg-slate-700"></div>
                        <div className="absolute bottom-0 left-[20%] right-0 h-[1px] bg-slate-200 dark:bg-slate-700"></div>
                        <div className="absolute bottom-0 left-[30%] right-0 h-[1px] bg-slate-200 dark:bg-slate-700"></div>
                        <div className="absolute bottom-0 left-0 h-[40px] w-full">
                          <svg viewBox="0 0 100 20" className="h-full w-full">
                            <path
                              d="M0,10 Q10,5 20,8 T40,6 T60,10 T80,12 T100,18"
                              fill="none"
                              stroke="#10b981"
                              strokeWidth="1"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Status Breakdown */}
              <Card>
                <div className="p-4">
                  <h5 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                    Status Breakdown
                  </h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Distribution of work items by status
                  </p>
                  <div className="space-y-4">
                    {project.statusBreakdown.map((item) => (
                      <div key={item.status} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className={`h-3 w-3 rounded-full ${
                                item.status === "On Track"
                                  ? "bg-emerald-500"
                                  : item.status === "Delayed"
                                    ? "bg-amber-500"
                                    : item.status === "Completed"
                                      ? "bg-blue-500"
                                      : "bg-rose-500"
                              }`}
                            ></div>
                            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{item.status}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{item.count}</span>
                            <span className="text-xs text-slate-600 dark:text-slate-400">({item.percentage}%)</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                          <div 
                            className={`h-2 rounded-full ${
                              item.status === "On Track"
                                ? "bg-emerald-500"
                                : item.status === "Delayed"
                                  ? "bg-amber-500"
                                  : item.status === "Completed"
                                    ? "bg-blue-500"
                                    : "bg-rose-500"
                            }`} 
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Upcoming Milestones */}
              <Card>
                <div className="p-4">
                  <h5 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                    Upcoming Milestones
                  </h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Key milestones in the next 30 days
                  </p>
                  <div className="space-y-4">
                    {project.upcomingMilestones.map((milestone) => (
                      <div
                        key={milestone.id}
                        className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-semibold text-slate-900 dark:text-white text-base">
                              {milestone.name}
                            </div>
                            <div className="mt-1 text-xs text-blue-600 dark:text-blue-400 font-medium">
                              {milestone.epicName}
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 flex items-center">
                            <Avatar rounded size="xs">
                              <div className="font-medium leading-none truncate">{milestone.owner.charAt(0)}</div>
                            </Avatar>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <HiOutlineCalendar className="h-3.5 w-3.5 text-slate-500" />
                          <span className="text-xs text-slate-600 dark:text-slate-400">{milestone.dueDate}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectDashboardPage 